import React, { useState } from 'react';
import { CalendarIcon, GlobeIcon, ClockIcon, CreditCardIcon, ThermometerIcon, MapPinIcon, CheckIcon } from 'lucide-react';
export function CountryInfo() {
  const [activeTab, setActiveTab] = useState('overview');
  return <section id="country-info" className="py-16 bg-white" data-id="element-39">
      <div className="container mx-auto px-4" data-id="element-40">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-41">
          {/* Left Column - Country Info */}
          <div className="lg:w-2/3" data-id="element-42">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-43">
              Switzerland
            </h2>
            {/* Tabs Navigation */}
            <div className="flex border-b border-gray-200 mb-8 overflow-x-auto hide-scrollbar" data-id="element-44">
              <button onClick={() => setActiveTab('overview')} className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${activeTab === 'overview' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-45">
                Overview
              </button>
              <button onClick={() => setActiveTab('when-to-visit')} className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${activeTab === 'when-to-visit' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-46">
                When to Visit
              </button>
              <button onClick={() => setActiveTab('getting-around')} className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${activeTab === 'getting-around' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-47">
                Getting Around
              </button>
              <button onClick={() => setActiveTab('food')} className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${activeTab === 'food' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-48">
                Food & Drink
              </button>
              <button onClick={() => setActiveTab('culture')} className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${activeTab === 'culture' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-49">
                Culture & Customs
              </button>
            </div>
            {/* Tab Content */}
            <div className="prose prose-lg max-w-none" data-id="element-50">
              {activeTab === 'overview' && <div data-id="element-51">
                  <p data-id="element-52">
                    Switzerland, a small Alpine country in the heart of Europe,
                    is renowned for its breathtaking mountain landscapes,
                    pristine lakes, picturesque villages, and world-class
                    cities. This landlocked nation shares borders with France,
                    Italy, Germany, Austria, and Liechtenstein, making it a
                    cultural crossroads with diverse influences.
                  </p>
                  <p data-id="element-53">
                    The Swiss Alps dominate the landscape, covering about 60% of
                    the country's total area. Famous peaks like the Matterhorn,
                    Eiger, and Jungfrau attract mountaineers and winter sports
                    enthusiasts from around the globe. The country is also home
                    to over 1,500 lakes, including Lake Geneva, Lake Zurich, and
                    Lake Lucerne, offering stunning vistas and recreational
                    opportunities.
                  </p>
                  <p data-id="element-54">
                    Switzerland is divided into 26 cantons, each with a high
                    degree of autonomy. This federal structure contributes to
                    the country's political stability and economic prosperity.
                    The nation operates with four official languages: German,
                    French, Italian, and Romansh, reflecting its cultural
                    diversity.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8 not-prose" data-id="element-55">
                    <div className="rounded-lg overflow-hidden" data-id="element-56">
                      <img src="https://images.unsplash.com/photo-1527668752968-14dc70a27c95?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80" alt="Matterhorn mountain in Switzerland" className="w-full h-64 object-cover" data-id="element-57" />
                    </div>
                    <div className="rounded-lg overflow-hidden" data-id="element-58">
                      <img src="https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80" alt="Lucerne lake and city view" className="w-full h-64 object-cover" data-id="element-59" />
                    </div>
                  </div>
                  <p data-id="element-60">
                    Beyond its natural beauty, Switzerland is famous for its
                    precision watchmaking, delicious chocolates, banking system,
                    and commitment to neutrality in international affairs. The
                    country consistently ranks among the top nations for quality
                    of life, healthcare, education, and innovation.
                  </p>
                  <p data-id="element-61">
                    Whether you're seeking adventure in the mountains,
                    relaxation by crystal-clear lakes, cultural experiences in
                    historic cities, or culinary delights, Switzerland offers an
                    unforgettable travel experience that combines natural
                    splendor with Swiss efficiency and hospitality.
                  </p>
                </div>}
              {activeTab === 'when-to-visit' && <div data-id="element-62">
                  <p data-id="element-63">
                    Switzerland is a year-round destination, with each season
                    offering its own unique charm and activities. The best time
                    to visit depends largely on what you want to experience
                    during your trip.
                  </p>
                  <h3 data-id="element-64">Summer (June to August)</h3>
                  <p data-id="element-65">
                    Summer is peak tourist season in Switzerland, with warm
                    temperatures ranging from 18-28°C (65-82°F). This is the
                    perfect time for hiking, mountain biking, paragliding, and
                    enjoying the numerous lakes. Alpine wildflowers are in full
                    bloom, creating spectacular landscapes. However, be prepared
                    for larger crowds and higher prices during this period.
                  </p>
                  <h3 data-id="element-66">Autumn (September to November)</h3>
                  <p data-id="element-67">
                    Fall brings beautiful foliage colors to the Swiss
                    landscapes. September still offers pleasant weather with
                    fewer tourists, making it an excellent time for hiking and
                    sightseeing. By October and November, temperatures cool down
                    (8-15°C/46-59°F), and some mountain facilities begin to
                    close in preparation for the winter season.
                  </p>
                  <h3 data-id="element-68">Winter (December to February)</h3>
                  <p data-id="element-69">
                    Winter transforms Switzerland into a snow-covered
                    wonderland. This is the prime season for skiing,
                    snowboarding, and other winter sports in world-class resorts
                    like Zermatt, St. Moritz, and Verbier. December features
                    festive Christmas markets throughout the country.
                    Temperatures in the lowlands range from 0-5°C (32-41°F),
                    while mountain areas are significantly colder.
                  </p>
                  <h3 data-id="element-70">Spring (March to May)</h3>
                  <p data-id="element-71">
                    Spring sees Switzerland gradually warming up, with
                    temperatures between 8-15°C (46-59°F). The season brings
                    lush greenery and blooming flowers to the valleys, while the
                    mountains still have snow for late-season skiing. This
                    shoulder season offers good value and fewer crowds, though
                    some mountain passes and hiking trails may still be closed
                    due to snow, especially in early spring.
                  </p>
                  <div className="bg-blue-50 p-6 rounded-lg my-6 not-prose" data-id="element-72">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-73">
                      Travel Tip: Swiss Weather
                    </h4>
                    <p className="text-gray-700" data-id="element-74">
                      Mountain weather can change rapidly regardless of season.
                      Always check forecasts before outdoor activities and pack
                      layers, even in summer. The higher elevations can be
                      significantly colder than valley locations, with
                      temperature drops of 5-10°C for every 1,000 meters of
                      elevation gain.
                    </p>
                  </div>
                </div>}
              {activeTab === 'getting-around' && <div data-id="element-75">
                  <p data-id="element-76">
                    Switzerland boasts one of the world's most efficient and
                    comprehensive public transportation systems, making it
                    incredibly easy to explore the country without a car.
                  </p>
                  <h3 data-id="element-77">Swiss Travel System</h3>
                  <p data-id="element-78">
                    The integrated Swiss Travel System connects virtually every
                    corner of the country through a network of trains, buses,
                    boats, and mountain transportation. Services run with
                    legendary Swiss punctuality, and connections are timed to
                    minimize waiting.
                  </p>
                  <h3 data-id="element-79">Trains</h3>
                  <p data-id="element-80">
                    The Swiss Federal Railways (SBB/CFF/FFS) operates an
                    extensive rail network that's the backbone of public
                    transportation. High-speed InterCity trains connect major
                    cities, while regional services reach smaller towns. Scenic
                    routes like the Glacier Express, Bernina Express, and
                    GoldenPass Line offer spectacular views through panoramic
                    windows.
                  </p>
                  <h3 data-id="element-81">Buses</h3>
                  <p data-id="element-82">
                    PostBus (PostAuto/CarPostal) services extend the public
                    transport network to villages and areas not served by
                    trains. These distinctive yellow buses are especially
                    valuable in mountain regions, where they navigate winding
                    roads with precision.
                  </p>
                  <h3 data-id="element-83">Boats and Ferries</h3>
                  <p data-id="element-84">
                    Lake steamers and ferry services operate on Switzerland's
                    major lakes, offering both practical transportation and
                    leisurely cruises. These boat journeys provide unique
                    perspectives of lakeside towns and mountain panoramas.
                  </p>
                  <h3 data-id="element-85">Mountain Transportation</h3>
                  <p data-id="element-86">
                    A vast network of cable cars, funiculars, and cogwheel
                    railways provides access to Switzerland's alpine regions.
                    These specialized transports are essential for reaching ski
                    areas, hiking trails, and mountain viewpoints.
                  </p>
                  <div className="bg-blue-50 p-6 rounded-lg my-6 not-prose" data-id="element-87">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-88">
                      Travel Tip: Swiss Travel Pass
                    </h4>
                    <p className="text-gray-700" data-id="element-89">
                      For visitors planning to use public transportation
                      extensively, the Swiss Travel Pass offers unlimited travel
                      on the Swiss Travel System network, including trains,
                      buses, and boats. It also provides free admission to over
                      500 museums and discounts on many mountain excursions.
                      Available for 3, 4, 8, or 15 consecutive days, it can be a
                      cost-effective option for exploring multiple regions.
                    </p>
                  </div>
                </div>}
              {activeTab === 'food' && <div data-id="element-90">
                  <p data-id="element-91">
                    Swiss cuisine reflects the country's diverse cultural
                    influences, with variations across its German, French, and
                    Italian-speaking regions. Traditional dishes often feature
                    local ingredients like cheese, potatoes, and fresh alpine
                    produce.
                  </p>
                  <h3 data-id="element-92">Iconic Swiss Dishes</h3>
                  <ul data-id="element-93">
                    <li data-id="element-94">
                      <strong data-id="element-95">Fondue:</strong> Perhaps Switzerland's most famous
                      culinary export, cheese fondue consists of melted cheese
                      (typically Gruyère and Emmental) mixed with white wine and
                      served in a communal pot. Diners dip bread cubes into the
                      bubbling mixture using long-stemmed forks.
                    </li>
                    <li data-id="element-96">
                      <strong data-id="element-97">Raclette:</strong> This dish involves melting
                      cheese and scraping it over boiled potatoes, pickled
                      onions, and gherkins. Traditionally, a half wheel of
                      cheese is heated at the edge of a fire, but modern
                      raclette grills are now common.
                    </li>
                    <li data-id="element-98">
                      <strong data-id="element-99">Rösti:</strong> A simple but delicious dish of
                      grated, pan-fried potatoes, rösti resembles a large potato
                      pancake. Originally a breakfast dish from the
                      German-speaking region, it's now enjoyed throughout
                      Switzerland.
                    </li>
                    <li data-id="element-100">
                      <strong data-id="element-101">Zürcher Geschnetzeltes:</strong> A specialty from
                      Zurich featuring thin strips of veal in a creamy mushroom
                      sauce, typically served with rösti.
                    </li>
                    <li data-id="element-102">
                      <strong data-id="element-103">Älplermagronen:</strong> Alpine macaroni, a hearty
                      combination of pasta, potatoes, cheese, cream, and onions,
                      often served with applesauce.
                    </li>
                  </ul>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-6 not-prose" data-id="element-104">
                    <div className="rounded-lg overflow-hidden" data-id="element-105">
                      <img src="https://images.unsplash.com/photo-1551218808-94e220e084d2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80" alt="Swiss cheese fondue" className="w-full h-48 object-cover" data-id="element-106" />
                      <p className="text-sm text-center mt-1" data-id="element-107">Cheese Fondue</p>
                    </div>
                    <div className="rounded-lg overflow-hidden" data-id="element-108">
                      <img src="https://images.unsplash.com/photo-1604507000698-57bfd3f0ae1f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80" alt="Swiss chocolate" className="w-full h-48 object-cover" data-id="element-109" />
                      <p className="text-sm text-center mt-1" data-id="element-110">
                        Swiss Chocolate
                      </p>
                    </div>
                    <div className="rounded-lg overflow-hidden" data-id="element-111">
                      <img src="https://images.unsplash.com/photo-1626200926749-cccc3d2caf12?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80" alt="Raclette cheese" className="w-full h-48 object-cover" data-id="element-112" />
                      <p className="text-sm text-center mt-1" data-id="element-113">Raclette</p>
                    </div>
                  </div>
                  <h3 data-id="element-114">Swiss Sweets and Desserts</h3>
                  <p data-id="element-115">
                    Switzerland is world-renowned for its chocolate, with brands
                    like Lindt, Toblerone, and Sprüngli enjoying international
                    fame. Beyond chocolate, traditional Swiss desserts include:
                  </p>
                  <ul data-id="element-116">
                    <li data-id="element-117">
                      <strong data-id="element-118">Meringues with Double Cream:</strong> Crisp
                      meringues served with the famously thick cream from
                      Gruyère.
                    </li>
                    <li data-id="element-119">
                      <strong data-id="element-120">Nusstorte:</strong> A caramelized nut tart from
                      the Engadine region.
                    </li>
                    <li data-id="element-121">
                      <strong data-id="element-122">Vermicelles:</strong> Chestnut puree pressed into
                      spaghetti-like strands and topped with whipped cream.
                    </li>
                  </ul>
                  <h3 data-id="element-123">Beverages</h3>
                  <p data-id="element-124">
                    Swiss wines, particularly from regions like Valais, Vaud,
                    and Ticino, are excellent but less known internationally as
                    most production is consumed domestically. Switzerland also
                    produces distinctive fruit brandies (Kirsch, Williams) and
                    has a growing craft beer scene. For non-alcoholic options,
                    Rivella (a milk serum-based soft drink) is uniquely Swiss.
                  </p>
                </div>}
              {activeTab === 'culture' && <div data-id="element-125">
                  <p data-id="element-126">
                    Switzerland's culture is a fascinating blend of influences
                    from its German, French, Italian, and Romansh heritage, yet
                    it maintains a distinctive Swiss character shaped by its
                    alpine environment, political neutrality, and tradition of
                    direct democracy.
                  </p>
                  <h3 data-id="element-127">Cultural Values</h3>
                  <p data-id="element-128">
                    Swiss society places high value on punctuality, precision,
                    cleanliness, and privacy. The Swiss tend to be reserved
                    initially but are genuinely friendly and helpful once
                    relationships are established. There's a strong emphasis on
                    consensus-building and community involvement, with many
                    decisions made at the local level through referendums.
                  </p>
                  <h3 data-id="element-129">Traditions and Customs</h3>
                  <p data-id="element-130">
                    Traditional Swiss culture remains vibrant, especially in
                    rural areas. Alpine traditions include:
                  </p>
                  <ul data-id="element-131">
                    <li data-id="element-132">
                      <strong data-id="element-133">Alpabzug/Désalpe:</strong> The ceremonial descent
                      of cattle from mountain pastures in autumn, with herders
                      and animals adorned in decorative gear.
                    </li>
                    <li data-id="element-134">
                      <strong data-id="element-135">Yodeling:</strong> Originally used for
                      communication across alpine valleys, now preserved as a
                      cultural art form.
                    </li>
                    <li data-id="element-136">
                      <strong data-id="element-137">Schwingen:</strong> Swiss wrestling, where
                      competitors wear special shorts and try to pin their
                      opponent's shoulders to the ground.
                    </li>
                    <li data-id="element-138">
                      <strong data-id="element-139">Fasnacht:</strong> Pre-Lenten carnival
                      celebrations, with the Basel Fasnacht being particularly
                      famous for its parades and lanterns.
                    </li>
                  </ul>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6 not-prose" data-id="element-140">
                    <div className="rounded-lg overflow-hidden" data-id="element-141">
                      <img src="https://images.unsplash.com/photo-1597047084993-bf337e09ede0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80" alt="Traditional Swiss alpine festival" className="w-full h-64 object-cover" data-id="element-142" />
                    </div>
                    <div className="rounded-lg overflow-hidden" data-id="element-143">
                      <img src="https://images.unsplash.com/photo-1534531173927-aeb928d54385?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80" alt="Swiss alpine horn player" className="w-full h-64 object-cover" data-id="element-144" />
                    </div>
                  </div>
                  <h3 data-id="element-145">Etiquette Tips</h3>
                  <ul data-id="element-146">
                    <li data-id="element-147">
                      Greetings are formal, with handshakes being the norm. In
                      French-speaking areas, friends may exchange cheek kisses.
                    </li>
                    <li data-id="element-148">
                      Punctuality is highly valued - arrive on time for
                      appointments and social gatherings.
                    </li>
                    <li data-id="element-149">
                      Maintain a moderate speaking volume in public places. Loud
                      conversations are considered disruptive.
                    </li>
                    <li data-id="element-150">
                      Remove shoes when entering someone's home unless told
                      otherwise.
                    </li>
                    <li data-id="element-151">
                      When dining, keep hands (but not elbows) on the table.
                      Wait for the host to begin eating.
                    </li>
                    <li data-id="element-152">
                      Recycling and proper waste disposal are taken seriously.
                      Follow local sorting guidelines.
                    </li>
                    <li data-id="element-153">
                      Respect quiet hours (typically 10 PM to 7 AM and sometimes
                      during lunch) in residential areas.
                    </li>
                  </ul>
                  <div className="bg-blue-50 p-6 rounded-lg my-6 not-prose" data-id="element-154">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-155">
                      Cultural Insight
                    </h4>
                    <p className="text-gray-700" data-id="element-156">
                      Switzerland's multilingual nature means cultural practices
                      can vary between regions. The German-speaking Swiss may
                      appear more reserved than their French or Italian-speaking
                      compatriots, but all share a deep appreciation for their
                      traditions and natural environment. When traveling between
                      regions, be aware of these subtle cultural shifts.
                    </p>
                  </div>
                </div>}
            </div>
          </div>
          {/* Right Column - Quick Facts */}
          <div className="lg:w-1/3" data-id="element-157">
            <div className="bg-gray-50 rounded-lg p-6 sticky top-24" data-id="element-158">
              <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-159">
                Quick Facts
              </h3>
              <div className="space-y-5" data-id="element-160">
                <div className="flex items-start" data-id="element-161">
                  <MapPinIcon size={20} className="text-blue-600 mt-1 mr-3 flex-shrink-0" data-id="element-162" />
                  <div data-id="element-163">
                    <h4 className="font-medium text-gray-900" data-id="element-164">Location</h4>
                    <p className="text-gray-600" data-id="element-165">
                      Central Europe, bordered by Germany, France, Italy,
                      Austria, and Liechtenstein
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-166">
                  <GlobeIcon size={20} className="text-blue-600 mt-1 mr-3 flex-shrink-0" data-id="element-167" />
                  <div data-id="element-168">
                    <h4 className="font-medium text-gray-900" data-id="element-169">Capital</h4>
                    <p className="text-gray-600" data-id="element-170">
                      Bern (de facto), with Zurich as the largest city
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-171">
                  <div size={20} className="text-blue-600 mt-1 mr-3 flex-shrink-0" data-id="element-172" />
                  <div data-id="element-173">
                    <h4 className="font-medium text-gray-900" data-id="element-174">Languages</h4>
                    <p className="text-gray-600" data-id="element-175">
                      German (63%), French (23%), Italian (8%), Romansh (0.5%)
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-176">
                  <CreditCardIcon size={20} className="text-blue-600 mt-1 mr-3 flex-shrink-0" data-id="element-177" />
                  <div data-id="element-178">
                    <h4 className="font-medium text-gray-900" data-id="element-179">Currency</h4>
                    <p className="text-gray-600" data-id="element-180">Swiss Franc (CHF)</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-181">
                  <ThermometerIcon size={20} className="text-blue-600 mt-1 mr-3 flex-shrink-0" data-id="element-182" />
                  <div data-id="element-183">
                    <h4 className="font-medium text-gray-900" data-id="element-184">Climate</h4>
                    <p className="text-gray-600" data-id="element-185">
                      Temperate, varying with altitude. Alpine in mountains,
                      continental in plateau regions
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-186">
                  <ClockIcon size={20} className="text-blue-600 mt-1 mr-3 flex-shrink-0" data-id="element-187" />
                  <div data-id="element-188">
                    <h4 className="font-medium text-gray-900" data-id="element-189">Time Zone</h4>
                    <p className="text-gray-600" data-id="element-190">
                      Central European Time (CET/CEST), GMT+1/+2
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-191">
                  <CalendarIcon size={20} className="text-blue-600 mt-1 mr-3 flex-shrink-0" data-id="element-192" />
                  <div data-id="element-193">
                    <h4 className="font-medium text-gray-900" data-id="element-194">
                      Best Time to Visit
                    </h4>
                    <p className="text-gray-600" data-id="element-195">
                      June-August for hiking, December-March for skiing
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-196">
                <h4 className="font-medium text-gray-900 mb-3" data-id="element-197">
                  Top Destinations
                </h4>
                <ul className="space-y-2" data-id="element-198">
                  <li className="flex items-start" data-id="element-199">
                    <CheckIcon size={16} className="text-green-600 mt-1 mr-2 flex-shrink-0" data-id="element-200" />
                    <span className="text-gray-600" data-id="element-201">Zurich</span>
                  </li>
                  <li className="flex items-start" data-id="element-202">
                    <CheckIcon size={16} className="text-green-600 mt-1 mr-2 flex-shrink-0" data-id="element-203" />
                    <span className="text-gray-600" data-id="element-204">Geneva</span>
                  </li>
                  <li className="flex items-start" data-id="element-205">
                    <CheckIcon size={16} className="text-green-600 mt-1 mr-2 flex-shrink-0" data-id="element-206" />
                    <span className="text-gray-600" data-id="element-207">Lucerne</span>
                  </li>
                  <li className="flex items-start" data-id="element-208">
                    <CheckIcon size={16} className="text-green-600 mt-1 mr-2 flex-shrink-0" data-id="element-209" />
                    <span className="text-gray-600" data-id="element-210">Interlaken</span>
                  </li>
                  <li className="flex items-start" data-id="element-211">
                    <CheckIcon size={16} className="text-green-600 mt-1 mr-2 flex-shrink-0" data-id="element-212" />
                    <span className="text-gray-600" data-id="element-213">Zermatt (Matterhorn)</span>
                  </li>
                  <li className="flex items-start" data-id="element-214">
                    <CheckIcon size={16} className="text-green-600 mt-1 mr-2 flex-shrink-0" data-id="element-215" />
                    <span className="text-gray-600" data-id="element-216">Jungfrau Region</span>
                  </li>
                  <li className="flex items-start" data-id="element-217">
                    <CheckIcon size={16} className="text-green-600 mt-1 mr-2 flex-shrink-0" data-id="element-218" />
                    <span className="text-gray-600" data-id="element-219">Bern</span>
                  </li>
                </ul>
              </div>
              <div className="mt-6" data-id="element-220">
                <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md transition-colors" data-id="element-221">
                  Download Travel Guide
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}